/**
 * KioskBoard — public full-screen clock-in/out station (Jun 2026).
 * Route: /kiosk/:token (no login — authenticated by the kiosk token).
 * Workers tap their name, enter their 4-digit PIN, and punch in/out.
 *
 * Extras:
 *  - Photo punch: when the kiosk has photo_on_punch enabled, a webcam
 *    frame is captured silently at each punch for buddy-punch review.
 *  - Offline mode: failed punches are queued in localStorage and synced
 *    automatically (POST /api/kiosk/{token}/punch-sync) when the
 *    connection returns. The last board is cached so names still render.
 *  - Multi-language: EN / ES / FR toggle, remembered per device.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Delete, LogIn, LogOut, Loader2, AlertTriangle, WifiOff, CloudUpload, Megaphone, ScanLine } from 'lucide-react';
import KioskBadgeScan from '../components/KioskBadgeScan';

const API = `${window.__BACKEND_URL__}/api`;

const queueKey = (token) => `hines_kiosk_queue_${token}`;
const boardKey = (token) => `hines_kiosk_board_${token}`;
const langKey = (token) => `hines_kiosk_lang_${token}`;

const readQueue = (token) => {
  try { return JSON.parse(localStorage.getItem(queueKey(token)) || '[]'); } catch { return []; }
};
const writeQueue = (token, q) => localStorage.setItem(queueKey(token), JSON.stringify(q));

const LANGS = ['en', 'es', 'fr'];
const LOCALES = { en: 'en-US', es: 'es-ES', fr: 'fr-CA', de: 'de-DE', it: 'it-IT' };
const STRINGS = {
  en: {
    tapToClockIn: 'Tap to clock in',
    onClockSince: (t) => `On the clock since ${t}`,
    onTheClock: (a, b) => `${a}/${b} on the clock`,
    pinToClockIn: 'Enter your PIN to clock IN',
    pinToClockOut: 'Enter your PIN to clock OUT',
    cancel: 'Cancel',
    checking: 'Checking…',
    noPin: 'No PIN set for this worker yet — ask your administrator.',
    genericErr: 'Something went wrong — try again.',
    clockedIn: (n) => `${n} clocked IN — have a great shift!`,
    clockedOut: (n, h) => `${n} clocked OUT${h != null ? ` — ${h}h logged` : ''}. See you tomorrow!`,
    queuedFlash: (n) => `${n}'s punch saved — it will sync when the connection returns.`,
    syncedFlash: (s, t) => `Back online — ${s}/${t} queued punch${t === 1 ? '' : 'es'} synced.`,
    offlineBanner: (q) => `No connection — punches are saved on this device${q > 0 ? ` (${q} queued)` : ''} and will sync automatically.`,
    queuedBanner: (q) => `${q} queued punch${q === 1 ? '' : 'es'} waiting to sync…`,
    syncingBanner: (q) => `Syncing ${q} queued punch${q === 1 ? '' : 'es'}…`,
    unavailable: 'Kiosk unavailable',
    noMembers: 'No team members yet — ask your administrator to add workers to this team.',
    footer: 'Hines Time Kiosk · punches sync to payroll automatically',
    scanBadge: 'Scan Badge',
    scanBadgeTitle: 'Badge Scanner',
    scanBadgeHint: 'Hold your badge QR (in the app under Kiosk → My Badge) up to the camera to punch in or out. No PIN needed.',
    scanBadgeDone: 'Done scanning',
    mealTitle: 'Did you take a meal break?',
    mealBody: 'You worked {n}+ hours without a logged meal break. Your company requires a 30-minute break for shifts of {n} or more hours.',
    mealDeduct: 'Yes — deduct 30-min unpaid break',
    mealNone: "No — I didn't take a break",
    mealCancel: 'Cancel',
  },
  es: {
    tapToClockIn: 'Toca para marcar entrada',
    onClockSince: (t) => `Trabajando desde las ${t}`,
    onTheClock: (a, b) => `${a}/${b} trabajando`,
    pinToClockIn: 'Ingresa tu PIN para marcar ENTRADA',
    pinToClockOut: 'Ingresa tu PIN para marcar SALIDA',
    cancel: 'Cancelar',
    checking: 'Verificando…',
    noPin: 'Este trabajador aún no tiene PIN — consulta a tu administrador.',
    genericErr: 'Algo salió mal — inténtalo de nuevo.',
    clockedIn: (n) => `${n} marcó ENTRADA — ¡buen turno!`,
    clockedOut: (n, h) => `${n} marcó SALIDA${h != null ? ` — ${h}h registradas` : ''}. ¡Hasta mañana!`,
    queuedFlash: (n) => `La marcación de ${n} se guardó — se sincronizará cuando vuelva la conexión.`,
    syncedFlash: (s, t) => `De vuelta en línea — ${s}/${t} marcaciones sincronizadas.`,
    offlineBanner: (q) => `Sin conexión — las marcaciones se guardan en este dispositivo${q > 0 ? ` (${q} en cola)` : ''} y se sincronizarán automáticamente.`,
    queuedBanner: (q) => `${q} marcación${q === 1 ? '' : 'es'} en cola esperando sincronizar…`,
    syncingBanner: (q) => `Sincronizando ${q} marcación${q === 1 ? '' : 'es'}…`,
    unavailable: 'Kiosco no disponible',
    noMembers: 'Aún no hay miembros — pide a tu administrador que agregue trabajadores a este equipo.',
    footer: 'Kiosco Hines Time · las marcaciones se sincronizan con la nómina automáticamente',
    scanBadge: 'Escanear credencial',
    scanBadgeTitle: 'Escáner de credenciales',
    scanBadgeHint: 'Acerca tu código QR de credencial (en la app: Kiosco → Mi credencial) a la cámara para marcar entrada o salida. Sin PIN.',
    scanBadgeDone: 'Terminar',
    mealTitle: '¿Tomaste tu descanso para comer?',
    mealBody: 'Trabajaste más de {n} horas sin un descanso registrado. Tu empresa exige un descanso de 30 minutos en turnos de {n} horas o más.',
    mealDeduct: 'Sí — descontar 30 min sin pago',
    mealNone: 'No — no tomé descanso',
    mealCancel: 'Cancelar',
  },
  fr: {
    tapToClockIn: 'Touchez pour pointer',
    onClockSince: (t) => `Au travail depuis ${t}`,
    onTheClock: (a, b) => `${a}/${b} au travail`,
    pinToClockIn: 'Entrez votre NIP pour pointer l\u2019ARRIVÉE',
    pinToClockOut: 'Entrez votre NIP pour pointer le DÉPART',
    cancel: 'Annuler',
    checking: 'Vérification…',
    noPin: 'Aucun NIP défini pour ce travailleur — demandez à votre administrateur.',
    genericErr: 'Une erreur est survenue — réessayez.',
    clockedIn: (n) => `${n} a pointé l\u2019ARRIVÉE — bon quart de travail !`,
    clockedOut: (n, h) => `${n} a pointé le DÉPART${h != null ? ` — ${h}h enregistrées` : ''}. À demain !`,
    queuedFlash: (n) => `Le pointage de ${n} est enregistré — il sera synchronisé au retour de la connexion.`,
    syncedFlash: (s, t) => `De retour en ligne — ${s}/${t} pointage${t === 1 ? '' : 's'} synchronisé${t === 1 ? '' : 's'}.`,
    offlineBanner: (q) => `Pas de connexion — les pointages sont enregistrés sur cet appareil${q > 0 ? ` (${q} en attente)` : ''} et seront synchronisés automatiquement.`,
    queuedBanner: (q) => `${q} pointage${q === 1 ? '' : 's'} en attente de synchronisation…`,
    syncingBanner: (q) => `Synchronisation de ${q} pointage${q === 1 ? '' : 's'}…`,
    unavailable: 'Kiosque indisponible',
    noMembers: 'Aucun membre pour le moment — demandez à votre administrateur d\u2019ajouter des travailleurs.',
    footer: 'Kiosque Hines Time · les pointages se synchronisent automatiquement avec la paie',
    scanBadge: 'Scanner le badge',
    scanBadgeTitle: 'Lecteur de badges',
    scanBadgeHint: 'Présentez le code QR de votre badge (dans l\u2019app : Kiosque → Mon badge) devant la caméra pour pointer. Aucun NIP requis.',
    scanBadgeDone: 'Terminé',
    mealTitle: 'Avez-vous pris votre pause-repas ?',
    mealBody: 'Vous avez travaillé plus de {n} heures sans pause enregistrée. Votre entreprise exige une pause de 30 minutes pour les quarts de {n} heures ou plus.',
    mealDeduct: 'Oui — déduire 30 min non payées',
    mealNone: 'Non — je n\u2019ai pas pris de pause',
    mealCancel: 'Annuler',
  },
};

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export default function KioskBoard() {
  const { token } = useParams();
  const [board, setBoard] = useState(null);
  const [err, setErr] = useState(null);
  const [offline, setOffline] = useState(false);
  const [queued, setQueued] = useState(() => readQueue(token).length);
  const [syncing, setSyncing] = useState(false);
  const [picked, setPicked] = useState(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [punchResult, setPunchResult] = useState(null);
  const [pinErr, setPinErr] = useState(null);
  const [scanMode, setScanMode] = useState(false);
  const [badgeErr, setBadgeErr] = useState(null);
  const [kioskMeal, setKioskMeal] = useState(null); // { kind, user_id, pin, badgeToken, name, shift_hours }
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem(langKey(token));
    return LANGS.includes(saved) ? saved : 'en';
  });
  const flashTimer = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const now = useClock();

  const t = STRINGS[lang];
  const locale = LOCALES[lang];

  const switchLang = (l) => {
    setLang(l);
    localStorage.setItem(langKey(token), l);
  };

  const fmtSince = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/kiosk/${token}/board`, { timeout: 12000 });
      setBoard(r.data);
      setErr(null);
      setOffline(false);
      localStorage.setItem(boardKey(token), JSON.stringify(r.data));
    } catch (e) {
      if (e.response) {
        setErr(e.response.data?.detail || 'Kiosk unavailable.');
      } else {
        setOffline(true);
        if (!board) {
          try {
            const cached = JSON.parse(localStorage.getItem(boardKey(token)) || 'null');
            if (cached) setBoard(cached);
            else setErr('No connection and no cached kiosk data yet.');
          } catch { setErr('No connection.'); }
        }
      }
    }
  }, [token]);

  const syncQueue = useCallback(async () => {
    const q = readQueue(token);
    if (q.length === 0 || syncing) return;
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/kiosk/${token}/punch-sync`, { punches: q }, { timeout: 20000 });
      writeQueue(token, []);
      setQueued(0);
      setOffline(false);
      setFlash({ synced: r.data.synced, total: r.data.total });
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 5000);
      load();
    } catch (e) {
      if (e.response && e.response.status === 404) { writeQueue(token, []); setQueued(0); }
    } finally {
      setSyncing(false);
    }
  }, [token, syncing, load]);

  useEffect(() => {
    load();
    const timer = setInterval(() => { load(); syncQueue(); }, 30000);
    const onOnline = () => { setOffline(false); load(); syncQueue(); };
    const onOffline = () => setOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [load, syncQueue]);

  useEffect(() => () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((tr) => tr.stop());
  }, []);

  // Webcam for photo-on-punch: start when the PIN pad opens, stop on close.
  useEffect(() => {
    if (!picked || !board?.photo_on_punch) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 480, facingMode: 'user' }, audio: false });
        if (cancelled) { stream.getTracks().forEach((tr) => tr.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch {
        /* camera denied/unavailable — punch proceeds without a photo */
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;
      }
    };
  }, [picked, board?.photo_on_punch]);

  // Escape key closes the PIN overlay (accessibility).
  useEffect(() => {
    if (!picked) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setPicked(null); setPin(''); setPinErr(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picked]);

  const capturePhoto = () => {
    try {
      const video = videoRef.current;
      if (!video || !streamRef.current || video.readyState < 2) return null;
      const canvas = document.createElement('canvas');
      const w = 320;
      const h = Math.round((video.videoHeight / video.videoWidth) * w) || 240;
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(video, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', 0.6);
    } catch {
      return null;
    }
  };

  const submitPin = async (fullPin) => {
    if (busy || !picked) return;
    setBusy(true);
    setPinErr(null);
    const photo = board?.photo_on_punch ? capturePhoto() : null;
    try {
      const r = await axios.post(
        `${API}/kiosk/${token}/punch`,
        { user_id: picked.id, pin: fullPin, ...(photo ? { photo_b64: photo } : {}) },
        { timeout: 15000 },
      );
      if (r.data?.action === 'meal_break_prompt') {
        // Alberta ESA: 5h+ shift, no logged break — ask before clocking out.
        setKioskMeal({ kind: 'pin', user_id: picked.id, pin: fullPin, name: r.data.worker_name, shift_hours: r.data.shift_hours, threshold: r.data.break_threshold_hours });
        setPicked(null);
        setPin('');
        return;
      }
      setPicked(null);
      setPin('');
      setFlash({ name: r.data.worker_name, action: r.data.action, hours: r.data.hours_worked });
      setPunchResult({
        name: r.data.worker_name,
        type: r.data.action === 'clock_in' ? 'in' : 'out',
        hours: r.data.hours_worked,
      });
      setTimeout(() => setPunchResult(null), 3000);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 4000);
      load();
    } catch (e) {
      if (!e.response) {
        const q = readQueue(token);
        q.push({ user_id: picked.id, pin: fullPin, at: new Date().toISOString() });
        writeQueue(token, q);
        setQueued(q.length);
        setOffline(true);
        const name = picked.name;
        setPicked(null);
        setPin('');
        setFlash({ name, action: 'queued' });
        if (flashTimer.current) clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlash(null), 5000);
      } else {
        setPin('');
        setPinErr(e.response.data?.detail || t.genericErr);
      }
    } finally {
      setBusy(false);
    }
  };

  const pressDigit = (d) => {
    if (busy) return;
    setPinErr(null);
    const next = (pin + d).slice(0, 4);
    setPin(next);
    if (next.length === 4) submitPin(next);
  };

  const resolveKioskMeal = async (deduct) => {
    if (!kioskMeal || busy) return;
    setBusy(true);
    try {
      const flags = { deduct_break: deduct, confirm_no_break: !deduct };
      const r = kioskMeal.kind === 'pin'
        ? await axios.post(`${API}/kiosk/${token}/punch`, { user_id: kioskMeal.user_id, pin: kioskMeal.pin, ...flags }, { timeout: 15000 })
        : await axios.post(`${API}/kiosk/${token}/badge-punch`, { badge_token: kioskMeal.badgeToken, ...flags }, { timeout: 15000 });
      setKioskMeal(null);
      setFlash({ name: r.data.worker_name, action: r.data.action, hours: r.data.hours_worked });
      setPunchResult({ name: r.data.worker_name, type: 'out', hours: r.data.hours_worked });
      setTimeout(() => setPunchResult(null), 3000);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 4000);
      load();
    } catch (e) {
      setKioskMeal(null);
      setPinErr(e.response?.data?.detail || t.genericErr);
    } finally {
      setBusy(false);
    }
  };

  const handleBadgeToken = async (badgeToken) => {
    setBadgeErr(null);
    try {
      const r = await axios.post(`${API}/kiosk/${token}/badge-punch`, { badge_token: badgeToken }, { timeout: 15000 });
      if (r.data?.action === 'meal_break_prompt') {
        setScanMode(false);
        setKioskMeal({ kind: 'badge', badgeToken, name: r.data.worker_name, shift_hours: r.data.shift_hours, threshold: r.data.break_threshold_hours });
        return;
      }
      setFlash({ name: r.data.worker_name, action: r.data.action, hours: r.data.hours_worked });
      setPunchResult({
        name: r.data.worker_name,
        type: r.data.action === 'clock_in' ? 'in' : 'out',
        hours: r.data.hours_worked,
      });
      setTimeout(() => setPunchResult(null), 3000);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 4000);
      load();
    } catch (e) {
      setBadgeErr(e.response?.data?.detail || t.genericErr);
      setTimeout(() => setBadgeErr(null), 5000);
    }
  };

  if (err && !board) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#0B1321' }} data-testid="kiosk-error-screen">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: '#FFA000' }} />
          <h1 className="text-xl font-bold text-white mb-2">{t.unavailable}</h1>
          <p className="text-sm" style={{ color: '#94A3B8' }}>{err}</p>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1321' }}>
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  const clockedIn = board.members.filter((m) => m.clocked_in).length;

  return (
    <div className="min-h-screen p-4 sm:p-8" style={{ background: '#0B1321' }} data-testid="kiosk-board">
      {/* Language toggle + badge scan */}
      <div className="max-w-6xl mx-auto flex justify-end items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => setScanMode(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors"
          style={{ background: 'rgba(30,136,229,0.15)', border: '1px solid rgba(30,136,229,0.5)', color: '#7BB8E8' }}
          data-testid="kiosk-scan-badge-btn"
        >
          <ScanLine className="w-3.5 h-3.5" />
          {t.scanBadge}
        </button>
        <div className="inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid #2D3748' }} data-testid="kiosk-lang-toggle">
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => switchLang(l)}
              className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors"
              style={{
                background: lang === l ? '#1E88E5' : '#121D2E',
                color: lang === l ? '#fff' : '#94A3B8',
              }}
              data-testid={`kiosk-lang-${l}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Offline banner */}
      {(offline || queued > 0) && (
        <div
          className="max-w-6xl mx-auto mb-4 rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm font-medium"
          style={{ background: 'rgba(255,160,0,0.1)', border: '1px solid rgba(255,160,0,0.4)', color: '#FFA000' }}
          data-testid="kiosk-offline-banner"
        >
          {syncing ? <CloudUpload className="w-4 h-4 animate-pulse" /> : <WifiOff className="w-4 h-4" />}
          {syncing ? t.syncingBanner(queued) : offline ? t.offlineBanner(queued) : t.queuedBanner(queued)}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 max-w-6xl mx-auto">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{board.team_name}</h1>
          {board.location_label && (
            <p className="flex items-center gap-1.5 text-sm mt-1" style={{ color: '#94A3B8' }}>
              <MapPin className="w-4 h-4" /> {board.location_label}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-4xl sm:text-5xl font-bold text-white font-mono" data-testid="kiosk-clock">
            {now.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}
          </p>
          <p className="text-sm" style={{ color: '#64748B' }}>
            {now.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' })}
            {' · '}{t.onTheClock(clockedIn, board.members.length)}
          </p>
          {board.weather && (
            <p className="flex items-center justify-end gap-1 text-sm mt-0.5" style={{ color: '#94A3B8' }} data-testid="kiosk-weather">
              {board.weather.icon && (
                <img
                  src={`https://openweathermap.org/img/wn/${board.weather.icon}.png`}
                  alt=""
                  className="w-8 h-8 -my-1.5"
                />
              )}
              <span className="font-semibold text-white">{board.weather.temp_c}°C</span>
              <span className="capitalize">· {board.weather.description}</span>
              {board.weather.wind_kmh > 0 && <span>· wind {board.weather.wind_kmh} km/h</span>}
            </p>
          )}
        </div>
      </div>

      {/* Announcement — message of the day set by the admin */}
      {board.announcement && (
        <div
          className="max-w-6xl mx-auto mb-6 rounded-xl px-5 py-4 flex items-start gap-3"
          style={{ background: 'rgba(30,136,229,0.10)', border: '1px solid rgba(30,136,229,0.45)' }}
          data-testid="kiosk-announcement"
        >
          <Megaphone className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#7BB8E8' }} />
          <p className="text-base sm:text-lg font-medium text-white whitespace-pre-wrap">{board.announcement}</p>
        </div>
      )}

      {/* Punch confirmation flash */}
      {flash && (
        <div
          className="max-w-6xl mx-auto mb-6 rounded-xl p-4 flex items-center gap-3 text-lg font-semibold"
          style={{
            background: flash.action === 'clock_in' ? 'rgba(0,200,150,0.12)'
              : flash.action === 'queued' ? 'rgba(255,160,0,0.12)'
              : 'rgba(30,136,229,0.12)',
            border: `1px solid ${flash.action === 'clock_in' ? 'rgba(0,200,150,0.5)'
              : flash.action === 'queued' ? 'rgba(255,160,0,0.5)'
              : 'rgba(30,136,229,0.5)'}`,
            color: flash.action === 'clock_in' ? '#00C896'
              : flash.action === 'queued' ? '#FFA000'
              : '#7BB8E8',
          }}
          data-testid="kiosk-punch-flash"
        >
          {flash.synced != null ? (
            <><CloudUpload className="w-6 h-6" /> {t.syncedFlash(flash.synced, flash.total)}</>
          ) : flash.action === 'queued' ? (
            <><WifiOff className="w-6 h-6" /> {t.queuedFlash(flash.name)}</>
          ) : flash.action === 'clock_in' ? (
            <><LogIn className="w-6 h-6" /> {t.clockedIn(flash.name)}</>
          ) : (
            <><LogOut className="w-6 h-6" /> {t.clockedOut(flash.name, flash.hours)}</>
          )}
        </div>
      )}

      {/* Member grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 max-w-6xl mx-auto">
        {board.members.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => { setPicked(m); setPin(''); setPinErr(m.has_pin ? null : t.noPin); }}
            className="w-full flex items-center gap-4 p-5 rounded-2xl transition-all active:scale-[0.98] text-left min-h-[88px]"
            style={{
              background: m.clocked_in ? 'rgba(0,200,150,0.08)' : '#1A2433',
              border: `2px solid ${m.clocked_in ? 'rgba(0,200,150,0.4)' : '#2D3748'}`,
            }}
            data-testid={`kiosk-member-${m.id}`}
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold text-white"
              style={{ background: m.clocked_in ? '#00C896' : '#1E3A5F' }}
            >
              {(m.name || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-white truncate">{m.name}</p>
              {m.clocked_in ? (
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="w-2 h-2 rounded-full animate-pulse flex-shrink-0" style={{ background: '#00C896' }} />
                  <p className="text-sm font-medium truncate" style={{ color: '#00C896' }}>
                    {t.onClockSince(fmtSince(m.clock_in_time))}
                  </p>
                </div>
              ) : (
                <p className="text-sm mt-0.5" style={{ color: '#64748B' }}>{t.tapToClockIn}</p>
              )}
            </div>
          </button>
        ))}
        {board.members.length === 0 && (
          <p className="col-span-full text-center py-12" style={{ color: '#64748B' }}>
            {t.noMembers}
          </p>
        )}
      </div>

      {/* PIN pad overlay */}
      {picked && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(4,10,20,0.85)', backdropFilter: 'blur(6px)' }}
          data-testid="kiosk-pin-overlay"
        >
          <div className="w-full max-w-xs rounded-2xl p-6" style={{ background: '#121D2E', border: '1px solid #2D3748' }}>
            <p className="text-center text-white text-lg font-semibold">{picked.name}</p>
            <p className="text-center text-xs mt-1 mb-4" style={{ color: '#94A3B8' }}>
              {picked.clocked_in ? t.pinToClockOut : t.pinToClockIn}
            </p>

            {/* Webcam preview for photo-on-punch */}
            {board.photo_on_punch && (
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-20 object-cover rounded-lg mb-3"
                style={{ background: '#0B1321' }}
                data-testid="kiosk-photo-preview"
              />
            )}

            {/* PIN dots */}
            <div className="flex justify-center items-center gap-4 mb-6" data-testid="kiosk-pin-dots">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className="rounded-full transition-all duration-150"
                  style={{
                    width: i < pin.length ? '18px' : '14px',
                    height: i < pin.length ? '18px' : '14px',
                    background: i < pin.length ? '#1E88E5' : 'rgba(255,255,255,0.15)',
                  }}
                />
              ))}
            </div>

            {pinErr && (
              <p className="text-center text-xs mb-3" style={{ color: '#EF4444' }} data-testid="kiosk-pin-error">{pinErr}</p>
            )}
            {busy && (
              <p className="text-center text-xs mb-3 flex items-center justify-center gap-2" style={{ color: '#94A3B8' }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t.checking}
              </p>
            )}

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-2">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => pressDigit(d)}
                  disabled={busy || !picked.has_pin}
                  className="flex items-center justify-center rounded-2xl active:scale-90 transition-all select-none min-w-[72px] min-h-[72px] disabled:opacity-40"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                  data-testid={`kiosk-key-${d}`}
                >
                  <span className="text-3xl font-bold text-white">{d}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setPicked(null); setPin(''); setPinErr(null); }}
                className="flex items-center justify-center rounded-2xl text-sm font-semibold active:scale-90 transition-all select-none min-w-[72px] min-h-[72px]"
                style={{ background: '#1A2433', color: '#94A3B8', border: '1px solid #2D3748' }}
                data-testid="kiosk-key-cancel"
              >
                {t.cancel}
              </button>
              <button
                type="button"
                onClick={() => pressDigit('0')}
                disabled={busy || !picked.has_pin}
                className="flex items-center justify-center rounded-2xl active:scale-90 transition-all select-none min-w-[72px] min-h-[72px] disabled:opacity-40"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                data-testid="kiosk-key-0"
              >
                <span className="text-3xl font-bold text-white">0</span>
              </button>
              <button
                type="button"
                onClick={() => { setPin((p) => p.slice(0, -1)); setPinErr(null); }}
                disabled={busy}
                className="flex items-center justify-center rounded-2xl active:scale-90 transition-all select-none min-w-[72px] min-h-[72px]"
                style={{ background: '#1A2433', color: '#94A3B8', border: '1px solid #2D3748' }}
                data-testid="kiosk-key-back"
              >
                <Delete className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-center text-[11px] mt-10" style={{ color: '#3B4A61' }}>
        {t.footer}
      </p>

      {/* Alberta ESA meal-break decision (kiosk clock-out) */}
      {kioskMeal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: 'rgba(4,10,20,0.9)', backdropFilter: 'blur(6px)' }}
          data-testid="kiosk-meal-break-overlay"
        >
          <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#121D2E', border: '1px solid #2D3748' }}>
            <p className="text-white text-lg font-semibold mb-1">{kioskMeal.name} — {t.mealTitle}</p>
            {kioskMeal.shift_hours ? (
              <p className="text-sm font-semibold mb-1" style={{ color: '#FFB84D' }} data-testid="kiosk-meal-hours">
                {kioskMeal.shift_hours} h
              </p>
            ) : null}
            <p className="text-sm mb-5" style={{ color: '#94A3B8' }}>{t.mealBody.replaceAll('{n}', String(kioskMeal.threshold || 5))}</p>
            <div className="space-y-2">
              <button
                onClick={() => resolveKioskMeal(true)}
                disabled={busy}
                className="w-full py-4 rounded-xl font-bold text-white disabled:opacity-60"
                style={{ background: '#1E88E5' }}
                data-testid="kiosk-meal-deduct-btn"
              >
                {t.mealDeduct}
              </button>
              <button
                onClick={() => resolveKioskMeal(false)}
                disabled={busy}
                className="w-full py-4 rounded-xl font-bold text-white disabled:opacity-60"
                style={{ background: '#2D3748' }}
                data-testid="kiosk-meal-none-btn"
              >
                {t.mealNone}
              </button>
              <button
                onClick={() => setKioskMeal(null)}
                disabled={busy}
                className="w-full py-2 rounded-xl text-sm font-semibold"
                style={{ color: '#64748B' }}
                data-testid="kiosk-meal-cancel-btn"
              >
                {t.mealCancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Badge scanner overlay (wall-scanner mode) */}
      <KioskBadgeScan
        open={scanMode}
        title={t.scanBadgeTitle}
        hint={t.scanBadgeHint}
        closeLabel={t.scanBadgeDone}
        errorMsg={badgeErr}
        onToken={handleBadgeToken}
        onClose={() => { setScanMode(false); setBadgeErr(null); }}
      />

      {/* Full-screen punch confirmation (auto-dismisses after 3s) */}
      {punchResult && (
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center text-center p-8"
          style={{ background: punchResult.type === 'in' ? '#0B2218' : '#1A1A2E' }}
          data-testid="kiosk-punch-result"
        >
          <div
            className="w-28 h-28 rounded-full flex items-center justify-center mb-6"
            style={{ background: punchResult.type === 'in' ? 'rgba(0,200,150,0.2)' : 'rgba(239,68,68,0.2)' }}
          >
            {punchResult.type === 'in'
              ? <LogIn className="w-14 h-14" style={{ color: '#00C896' }} />
              : <LogOut className="w-14 h-14" style={{ color: '#EF4444' }} />}
          </div>
          <p className="text-5xl font-bold text-white mb-3">{punchResult.name}</p>
          <p
            className="text-2xl font-semibold mb-2"
            style={{ color: punchResult.type === 'in' ? '#00C896' : '#EF4444' }}
          >
            {punchResult.type === 'in' ? '✓ Clocked In' : '✓ Clocked Out'}
          </p>
          {punchResult.hours != null && (
            <p className="text-lg" style={{ color: '#64748B' }}>{punchResult.hours}h logged today</p>
          )}
          <p className="text-sm mt-8" style={{ color: '#475569' }}>Returning in a moment…</p>
        </div>
      )}
    </div>
  );
}
